import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { COMMS_LIMITS } from "@/lib/comms-limits";
import { leavesGap, prependEarlierPage, refreshPagedList, type PagedList } from "@/lib/keyset-list";
import { apiFetch } from "@/services/http/api-fetch";
import { useAuthStore } from "@/stores/authStore";

type Contact = {
  userId: string;
  name: string | null;
  email: string;
  /** False for a past conversation that can be read but no longer answered (COMM-13). */
  canMessage?: boolean;
};

type Conversation = Contact & {
  lastMessage: { body: string; createdAt: string; fromMe: boolean };
  unreadCount: number;
};

type Message = {
  id: string;
  senderId: string;
  recipientId: string;
  body: string;
  createdAt: string;
  readAt?: string | null;
  /** COMMS-14: "ai_escalation" carries the question/answer as data; absent on old rows. */
  kind?: "text" | "ai_escalation";
  escalation?: {
    question: string;
    aiAnswer: string;
    lessonTitle: string | null;
    /** I-4 / R-8: set when the tutor refused; the line is rendered in the reader's language. */
    refusalCode?: string | null;
  } | null;
};

type ThreadPage = { messages: Message[]; nextCursor?: string | null };

/** COMMS-11: how often the open conversation and the list refresh while the tab is visible. */
export const MESSAGES_POLL_MS = 60_000;

/**
 * R-15: a refreshed newest page merged into the conversation on screen. The server's order is kept
 * (its microsecond timestamps are finer than `createdAt`); when more arrived than one page holds,
 * the conversation restarts from the newest page so "Show earlier" fills the rest — never a gap.
 */
function refreshThread(current: PagedList<Message>, page: PagedList<Message>): PagedList<Message> {
  if (leavesGap(current.rows, page.rows, page.cursor)) return page;
  return refreshPagedList(current, page, "oldestFirst");
}

type MessagesInboxProps = {
  /** Conversation to open on load — `/messages?with=<userId>` from a notification (COMM-5). */
  initialContactId?: string | null;
  /** Lets the page keep `?with=` in step with the open conversation. */
  onSelectContact?: (userId: string) => void;
};

const displayName = (contact: Contact) => contact.name ?? contact.email;

const byName = (a: Contact, b: Contact) =>
  displayName(a).localeCompare(displayName(b), undefined, { sensitivity: "base" });

/** "5 minutes ago", "yesterday" … in the app's language (COMM-5). */
export function formatRelativeTime(iso: string, locale: string, now = Date.now()): string {
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000);
  const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 31_536_000],
    ["month", 2_592_000],
    ["week", 604_800],
    ["day", 86_400],
    ["hour", 3_600],
    ["minute", 60],
  ];
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  }
  return format.format(seconds, "second");
}

export function MessagesInbox({ initialContactId = null, onSelectContact }: MessagesInboxProps) {
  const t = useTranslations("messaging");
  const tRoot = useTranslations();
  const locale = useLocale();
  const intlLocale = locale === "hi" ? "hi-IN" : "en-IN";
  const currentUserId = useAuthStore((state) => state.user?.id ?? null);
  const errorText = useCallback(
    (err: unknown, fallbackKey: string) =>
      apiErrorText(err, tRoot, fallbackKey, { namespace: "messaging.errors" }),
    [tRoot],
  );
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  // COMMS-12: the server sends the newest page of a conversation; `cursor` pages further back.
  const [thread, setThread] = useState<PagedList<Message>>({ rows: [], cursor: null });
  const messages = thread.rows;
  const earlierCursor = thread.cursor;
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const loadingEarlierRef = useRef(false);
  // R-14: a failed "Show earlier" is shown by its button; a failed load clears once one works.
  const [earlierError, setEarlierError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const threadRef = useRef<HTMLUListElement>(null);
  const openedInitial = useRef(false);
  // COMMS-11: the conversation on screen, and the request loading it. A response for any other
  // conversation (the user clicked on before it arrived) is dropped, never rendered under the
  // new contact's name and compose box.
  const selectedRef = useRef<string | null>(null);
  const threadRequest = useRef<AbortController | null>(null);
  const newestShownId = useRef<string | null>(null);

  const loadContacts = useCallback(async () => {
    try {
      const res = await apiFetch("/v1/messages/contacts");
      if (!res.ok) throw await readApiErrorResponse(res);
      const json = (await res.json()) as { data: { contacts?: Contact[] } };
      return json.data.contacts ?? [];
    } catch (err) {
      setLoadError(errorText(err, "messaging.loadFailed"));
      return [];
    }
  }, [errorText]);

  const loadConversations = useCallback(async () => {
    try {
      const res = await apiFetch("/v1/messages/conversations");
      if (!res.ok) throw await readApiErrorResponse(res);
      const json = (await res.json()) as { data: { conversations?: Conversation[] } };
      setConversations(json.data.conversations ?? []);
      setLoadError(null);
    } catch (err) {
      setLoadError(errorText(err, "messaging.loadFailed"));
    }
  }, [errorText]);

  /** Fetches one page of a conversation; null when it is no longer the open one. */
  const fetchThreadPage = useCallback(
    async (userId: string, before: string | null, signal?: AbortSignal) => {
      const query = before ? `?before=${encodeURIComponent(before)}` : "";
      const res = await apiFetch(`/v1/messages/conversations/${userId}${query}`, { signal });
      if (!res.ok) throw await readApiErrorResponse(res);
      const json = (await res.json()) as { data: ThreadPage };
      return selectedRef.current === userId ? json.data : null;
    },
    [],
  );

  const markRead = useCallback(async (userId: string) => {
    // Best effort: an unread marker that fails to clear must not hide the conversation.
    const read = await apiFetch(`/v1/messages/conversations/${userId}/read`, {
      method: "POST",
    }).catch(() => null);
    if (read?.ok) {
      setConversations((current) =>
        current.map((row) => (row.userId === userId ? { ...row, unreadCount: 0 } : row)),
      );
    }
  }, []);

  /**
   * The newest page of `userId`'s conversation. `refresh` (polling, after a send) merges it into
   * what is shown, keeping earlier pages the user loaded; otherwise it replaces the thread.
   */
  const loadConversation = useCallback(
    async (userId: string, mode: "open" | "refresh" = "open") => {
      if (mode === "open") threadRequest.current?.abort();
      const controller = new AbortController();
      if (mode === "open") threadRequest.current = controller;
      let page: ThreadPage | null;
      try {
        page = await fetchThreadPage(userId, null, controller.signal);
      } catch (err) {
        if ((err as Error)?.name === "AbortError" || selectedRef.current !== userId) return;
        setLoadError(errorText(err, "messaging.loadFailed"));
        return;
      }
      if (!page) return;
      const fetched = page.messages;
      const fresh = { rows: fetched, cursor: page.nextCursor ?? null };
      setThread((current) => (mode === "open" ? fresh : refreshThread(current, fresh)));
      setLoadError(null);
      const unreadFromThem = fetched.some(
        (message) => message.senderId === userId && !message.readAt,
      );
      if (mode === "open" || unreadFromThem) await markRead(userId);
    },
    [errorText, fetchThreadPage, markRead],
  );

  async function loadEarlier() {
    const userId = selectedUserId;
    const cursor = earlierCursor;
    if (!userId || !cursor || loadingEarlierRef.current) return;
    loadingEarlierRef.current = true;
    setLoadingEarlier(true);
    setEarlierError(null);
    try {
      const page = await fetchThreadPage(userId, cursor);
      if (!page) return;
      // R-15: a refresh that restarted the conversation (a gap) made this page stale.
      setThread((current) =>
        current.cursor !== cursor
          ? current
          : {
              rows: prependEarlierPage(current.rows, page.messages),
              cursor: page.nextCursor ?? null,
            },
      );
    } catch (err) {
      if (selectedRef.current === userId) setEarlierError(errorText(err, "messaging.loadFailed"));
    } finally {
      loadingEarlierRef.current = false;
      setLoadingEarlier(false);
    }
  }

  const selectContact = useCallback(
    async (userId: string) => {
      selectedRef.current = userId;
      setSelectedUserId(userId);
      setError(null);
      setLoadError(null);
      setEarlierError(null);
      setThread({ rows: [], cursor: null });
      onSelectContact?.(userId);
      await loadConversation(userId);
    },
    [loadConversation, onSelectContact],
  );

  // COMMS-11: new messages show up without a reload — the open conversation and the list
  // refresh every minute while the tab is visible (as the notification centre does).
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      void loadConversations();
      const open = selectedRef.current;
      if (open) void loadConversation(open, "refresh");
    };
    const timer = window.setInterval(refresh, MESSAGES_POLL_MS);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [loadConversation, loadConversations]);

  useEffect(() => () => threadRequest.current?.abort(), []);

  useEffect(() => {
    void (async () => {
      const [loaded] = await Promise.all([loadContacts(), loadConversations()]);
      setContacts(loaded);
      setLoading(false);
    })();
  }, [loadContacts, loadConversations]);

  useEffect(() => {
    if (loading || openedInitial.current || !initialContactId) return;
    openedInitial.current = true;
    void selectContact(initialContactId);
  }, [loading, initialContactId, selectContact]);

  // Newest message in view after opening, sending or a new arrival — not when earlier messages
  // are prepended above.
  useEffect(() => {
    const newest = messages.at(-1)?.id ?? null;
    if (newest === newestShownId.current) return;
    newestShownId.current = newest;
    const list = threadRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages]);

  async function handleSend(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedUserId || !draft.trim()) return;

    setSending(true);
    setError(null);
    try {
      const res = await apiFetch(`/v1/messages/conversations/${selectedUserId}`, {
        method: "POST",
        body: JSON.stringify({ body: draft.trim() }),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      setDraft("");
      await Promise.all([loadConversation(selectedUserId, "refresh"), loadConversations()]);
    } catch (err) {
      setError(errorText(err, "messaging.sendFailed"));
    } finally {
      setSending(false);
    }
  }

  const query = search.trim().toLowerCase();
  const matches = useCallback(
    (contact: Contact) =>
      !query ||
      (contact.name ?? "").toLowerCase().includes(query) ||
      contact.email.toLowerCase().includes(query),
    [query],
  );
  const visibleConversations = useMemo(
    () => conversations.filter(matches),
    [conversations, matches],
  );
  const otherContacts = useMemo(() => {
    const inConversation = new Set(conversations.map((row) => row.userId));
    return contacts
      .filter((contact) => !inConversation.has(contact.userId) && matches(contact))
      .sort(byName);
  }, [contacts, conversations, matches]);

  if (loading) {
    return <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>;
  }

  const selected =
    contacts.find((contact) => contact.userId === selectedUserId) ??
    conversations.find((row) => row.userId === selectedUserId);
  const canReply = selected?.canMessage !== false;
  const selectedName = selected ? displayName(selected) : "";
  const bannerText = error ?? loadError;
  const errorBanner = bannerText ? (
    <p role="alert" className="text-sm text-red-600" data-testid="message-error">
      {bannerText}
    </p>
  ) : null;

  const contactButtonClass = (userId: string) =>
    `w-full rounded px-2 py-2 text-left text-sm ${selectedUserId === userId ? " bg-[var(--accent-soft)]" : "hover:bg-[var(--surface-3)]"}`;

  const escalationAnswerText = (escalation: NonNullable<Message["escalation"]>) => {
    if (escalation.refusalCode) {
      return escalation.refusalCode === "OUT_OF_SCOPE"
        ? t("escalation.declinedOutOfScope")
        : t("escalation.declined");
    }
    return escalation.aiAnswer.trim() || t("escalation.noAnswer");
  };

  const formatMessageTime = (iso: string) =>
    new Date(iso).toLocaleString(intlLocale, { dateStyle: "medium", timeStyle: "short" });

  return (
    <div className="grid gap-6 md:grid-cols-[280px_1fr]" data-testid="messages-inbox">
      <aside>
        {contacts.length === 0 && conversations.length === 0 ? (
          <>
            <h2 className="text-sm font-semibold">{t("contacts")}</h2>
            <p className="mt-3 text-sm text-[var(--text-muted)]">{t("noContacts")}</p>
          </>
        ) : (
          <>
            <label className="block text-sm">
              <span className="sr-only">{t("searchContacts")}</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("searchContacts")}
                className="w-full rounded border border-[var(--border-strong)] bg-[var(--surface-1)] px-3 py-2 text-sm"
                data-testid="messages-search"
              />
            </label>

            {visibleConversations.length > 0 ? (
              <>
                <h2 className="mt-4 text-sm font-semibold">{t("conversations")}</h2>
                <ul className="mt-2 space-y-1" data-testid="message-conversations">
                  {visibleConversations.map((row) => (
                    <li key={row.userId}>
                      <button
                        type="button"
                        onClick={() => void selectContact(row.userId)}
                        className={contactButtonClass(row.userId)}
                        data-testid={`message-contact-${row.userId}`}
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className={row.unreadCount > 0 ? "font-semibold" : undefined}>
                            {displayName(row)}
                          </span>
                          <time
                            dateTime={row.lastMessage.createdAt}
                            className="shrink-0 text-xs text-[var(--text-muted)]"
                          >
                            {formatRelativeTime(row.lastMessage.createdAt, intlLocale)}
                          </time>
                        </span>
                        <span className="mt-0.5 flex items-center justify-between gap-2">
                          <span className="truncate text-xs text-[var(--text-muted)]">
                            {row.lastMessage.fromMe
                              ? t("youSnippet", { body: row.lastMessage.body })
                              : row.lastMessage.body}
                          </span>
                          {row.unreadCount > 0 ? (
                            <span
                              className="shrink-0 rounded-full bg-[var(--ink-9)] px-2 text-xs text-white"
                              aria-label={t("unreadBadge", { count: row.unreadCount })}
                              data-testid={`message-unread-${row.userId}`}
                            >
                              {row.unreadCount}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {otherContacts.length > 0 ? (
              <>
                <h2 className="mt-4 text-sm font-semibold">
                  {conversations.length > 0 ? t("otherContacts") : t("contacts")}
                </h2>
                <ul className="mt-2 space-y-1" data-testid="message-other-contacts">
                  {otherContacts.map((contact) => (
                    <li key={contact.userId}>
                      <button
                        type="button"
                        onClick={() => void selectContact(contact.userId)}
                        className={contactButtonClass(contact.userId)}
                        data-testid={`message-contact-${contact.userId}`}
                      >
                        {displayName(contact)}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {query && visibleConversations.length === 0 && otherContacts.length === 0 ? (
              <p className="mt-3 text-sm text-[var(--text-muted)]">{t("noMatches")}</p>
            ) : null}
          </>
        )}
      </aside>

      <section>
        {!selectedUserId ? (
          <>
            <p className="text-sm text-[var(--text-muted)]">{t("selectContact")}</p>
            {errorBanner}
          </>
        ) : (
          <>
            {selectedName ? (
              <h2 className="mb-2 text-sm font-semibold" data-testid="message-thread-title">
                {selectedName}
              </h2>
            ) : null}
            {earlierError ? (
              <p
                role="alert"
                className="mb-1 text-xs text-red-600"
                data-testid="message-earlier-error"
              >
                {earlierError}
              </p>
            ) : null}
            {earlierCursor ? (
              <button
                type="button"
                className="mb-2 text-xs underline disabled:opacity-50"
                disabled={loadingEarlier}
                onClick={() => void loadEarlier()}
                data-testid="message-load-earlier"
              >
                {t("loadEarlier")}
              </button>
            ) : null}
            <ul
              ref={threadRef}
              className="mb-4 flex max-h-96 flex-col gap-2 overflow-y-auto rounded border border-[var(--border)] p-4"
              data-testid="message-thread"
            >
              {messages.map((message) => {
                const mine = message.senderId === currentUserId;
                return (
                  <li
                    key={message.id}
                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${mine ? "ml-auto bg-[var(--accent-soft)] text-right" : "mr-auto bg-[var(--surface-3)]"}`}
                    data-from={mine ? "me" : "them"}
                    data-testid={`message-item-${message.id}`}
                  >
                    <p className="text-xs font-semibold">{mine ? t("you") : selectedName}</p>
                    {message.kind === "ai_escalation" && message.escalation ? (
                      <div className="space-y-1 text-left" data-testid="message-escalation">
                        <p>{t("escalation.intro")}</p>
                        {message.escalation.lessonTitle ? (
                          <p className="text-xs">
                            {t("escalation.lesson", { title: message.escalation.lessonTitle })}
                          </p>
                        ) : null}
                        <p className="text-xs font-semibold">{t("escalation.question")}</p>
                        <p className="whitespace-pre-wrap">{message.escalation.question}</p>
                        <p className="text-xs font-semibold">{t("escalation.answer")}</p>
                        <p className="whitespace-pre-wrap">
                          {escalationAnswerText(message.escalation)}
                        </p>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap text-left">{message.body}</p>
                    )}
                    <time
                      dateTime={message.createdAt}
                      className="mt-1 block text-xs text-[var(--text-muted)]"
                    >
                      {formatMessageTime(message.createdAt)}
                    </time>
                  </li>
                );
              })}
            </ul>
            {canReply ? (
              <form onSubmit={handleSend} className="space-y-2">
                <textarea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  rows={3}
                  maxLength={COMMS_LIMITS.directMessage}
                  className="w-full rounded border px-3 py-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
                  placeholder={t("messagePlaceholder")}
                  data-testid="message-compose"
                />
                {errorBanner}
                <button
                  type="submit"
                  disabled={sending || !draft.trim()}
                  className="rounded bg-[var(--ink-9)] px-4 py-2 text-sm text-white disabled:opacity-50"
                  data-testid="message-send"
                >
                  {sending ? t("sending") : t("send")}
                </button>
              </form>
            ) : (
              <>
                <p className="text-sm text-[var(--text-muted)]" data-testid="message-closed">
                  {t("closed")}
                </p>
                {errorBanner}
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}
